// public/v3/src/components/field-context-rail.jsx — generic field-context rail.
//
// Provider-AGNOSTIC decision-support rail for the active form-fill field. It
// renders a normalized field-context object (source/value/rationale/confidence/
// provenance/contextBlocks/options/search) and knows NOTHING about "coverage" or
// "PA" specifically — a lab provider emits a coverage contextBlock + covered
// options; a future PA provider emits pa_criteria blocks + formulary options and
// this component renders both unchanged. It is READ-AND-CHOOSE: picking an option
// or typing in the search calls onPick(value) which fills the field's input —
// the clinician still presses Confirm. NEVER auto-fills.
//
// Mirrors status-rail.jsx's two modes: 'rail' (desktop right column) and 'band'
// (iPad collapsible). CSS in base.css (.field-context-rail*) + ipad.css band.
import React, { useState, useEffect, useRef } from 'react';
import { coverageFlagText } from '../lib/coverage-flag';

// Map a contextBlock severity to a tone class. Three distinct unverified tones:
// green = grounded 'covered'; amber 'unverified' = vendor decision-support; and
// INDIGO 'fcr-block-ai' = AI best-guess (keyed off kind:'ai_advisory' below, not
// severity, so an AI block stays indigo even though it carries severity:'unverified').
const SEVERITY_CLASS = {
  ok: 'fcr-block-ok',           // grounded covered
  warn: 'fcr-block-warn',       // ABN-may-apply / no_med_nec
  danger: 'fcr-block-danger',   // denied
  unverified: 'fcr-block-unverified', // vendor decision-support — amber, never green
  info: 'fcr-block-info',
};
const CONFIDENCE_LABEL = { high: 'High confidence', med: 'Medium confidence', low: 'Low confidence' };
const SOURCE_LABEL = {
  identity: 'Elation (chart/practice)', // deterministic demographic/office/provider prefill
  chart: 'Chart suggestion',
  manual: 'Manually entered',
  none: 'Not filled',
  needs_clinician: 'Needs clinician',
};

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

// props:
//   field        — the active field { id, label, fieldType, alignSource? }
//   answer       — answers[field.id] { value, source, rationale, confidence, coverage? }
//   context      — { options, search, coverageChecked, contextBlocks? } from fetchFieldOptions (null while loading)
//   loading      — bool (the upfront fetch is in flight)
//   onPick(value)— fill the field's direct input (does NOT confirm)
//   onSearch(q)  — re-fetch options for a typed query (provider decides if it searches)
//   mode         — 'rail' | 'band'; expanded/onToggleExpanded for band
export function FieldContextRail(props) {
  const { field, answer, context, loading, onPick, onSearch, mode = 'rail', expanded = true, onToggleExpanded } = props;
  const [term, setTerm] = useState('');
  const debounced = useDebounced(term, 300);
  const lastSearched = useRef(null);

  const searchEnabled = !!(context && context.search && context.search.enabled);
  useEffect(() => {
    if (!searchEnabled) return;
    if (debounced === lastSearched.current) return;
    lastSearched.current = debounced;
    onSearch && onSearch(debounced);
  }, [debounced, searchEnabled]);

  // Reset the typed term when the active field changes.
  useEffect(() => { setTerm(''); lastSearched.current = null; }, [field && field.id]);

  if (!field) return null;

  const a = answer || {};
  const value = a.value != null && a.value !== '' ? String(a.value) : null;
  const source = a.source || (context && context.source) || 'needs_clinician';
  const rationale = a.rationale || null;
  const confidence = a.confidence || null;
  const alignSource = field.alignSource || null;
  const blocks = (context && context.contextBlocks) || [];
  const options = (context && context.options) || [];
  const chartOptions = options.filter(o => o.onChart);
  const otherOptions = options.filter(o => !o.onChart);
  // An ON-CHART option is not automatically a COVERED one. The provider now
  // consults the patient's problem list for EVERY test (F5), so a charted code
  // can ride an ungrounded tier — an age/sex candidate list, a lab's courtesy
  // list — for a test no coverage source maps. "This patient's covered
  // diagnoses" is a coverage claim: make it only when every option in the group
  // carries status:'covered'; otherwise say what is actually known, which is
  // that these are on the chart.
  const chartGrounded = chartOptions.length > 0 && chartOptions.every(o => o.status === 'covered');
  // Coverage flag on the SEEDED suggestion: states the exact factor — whether the
  // suggested code does or does not satisfy medical necessity for THIS test. Drives
  // the "Why" the clinician sees. Null when coverage wasn't checked → no flag.
  const covFlag = coverageFlagText(a.coverage && a.coverage.status, value, field.label);
  const COV_TONE_CLASS = { ok: 'fcr-block-ok', warn: 'fcr-block-warn', danger: 'fcr-block-danger' };

  // Collapsed iPad band: a single toggle row.
  if (mode === 'band' && !expanded) {
    return (
      <div className="field-context-rail fcr-band fcr-band-collapsed">
        <b className="fcr-band-title">Decision support</b>
        <button className="fcr-band-toggle" onClick={onToggleExpanded} aria-label="Expand decision support">▾</button>
      </div>
    );
  }

  const wrapClass = mode === 'band' ? 'field-context-rail fcr-band' : 'field-context-rail';

  return (
    <div className={wrapClass} aria-label="Decision support">
      <div className="fcr-head">
        <b className="fcr-title">Decision support</b>
        {mode === 'band' && <button className="fcr-band-toggle" onClick={onToggleExpanded} aria-label="Collapse">▴</button>}
      </div>

      {/* Source & value */}
      <div className="fcr-section">
        <div className="fcr-kv"><span className="fcr-k">Suggested</span>
          <span className="fcr-v">{value || <em className="fcr-blank">blank — choose below</em>}</span></div>
        <div className="fcr-kv"><span className="fcr-k">Source</span><span className={`fcr-v fcr-source fcr-source-${source}`}>{SOURCE_LABEL[source] || source}</span></div>
        {confidence && <div className="fcr-kv"><span className="fcr-k">Confidence</span>
          <span className={`fcr-conf fcr-conf-${confidence}`}>{CONFIDENCE_LABEL[confidence] || confidence}</span></div>}
      </div>

      {/* Coverage flag — states the EXACT factor: does the suggested code satisfy
          medical necessity for THIS test? The actionable "Why" (e.g. "Z79.899 is not
          a listed covered indication for IRON TIBC — pick a covered diagnosis"). */}
      {covFlag && (
        <div className={`fcr-block ${COV_TONE_CLASS[covFlag.tone] || 'fcr-block-warn'}`} role="note">
          <b className="fcr-block-title">Why</b>
          <p className="fcr-block-body">{covFlag.text}</p>
        </div>
      )}

      {/* Rationale — the model's chart-derived reasoning ("should I override?") */}
      {rationale && (
        <div className="fcr-section fcr-rationale">
          <div className="fcr-k">{covFlag ? 'Model note' : 'Why'}</div>
          <p className="fcr-rationale-body">{rationale}</p>
        </div>
      )}

      {/* Provenance — the alignSource "position estimated" flag */}
      {(alignSource === 'vision' || alignSource === 'grid') && (
        <div className="fcr-block fcr-block-warn" role="note">
          <b className="fcr-block-title">Position estimated</b>
          <p className="fcr-block-body">This answer's position was estimated, not measured — verify it in the preview before signing.</p>
        </div>
      )}

      {/* Provider contextBlocks (coverage verdict, ai_advisory, vendor_advisory, demographic_likely, pa_criteria, …) */}
      {blocks.map((b, i) => {
        // AI best-guess gets INDIGO; vendor keeps amber; demographic_likely gets a soft
        // green-ish info panel; everything else keys off severity.
        const blockClass = b.kind === 'ai_advisory'
          ? 'fcr-block-ai'
          : (b.kind === 'demographic_likely'
            ? 'fcr-block-demographic'
            : (SEVERITY_CLASS[b.severity] || 'fcr-block-info'));
        return (
        <div key={i} className={`fcr-block ${blockClass}`} role="note">
          {b.kind === 'ai_advisory' && <span className="fcr-ai-tag">AI best guess — not grounded, verify</span>}
          {b.kind === 'vendor_advisory' && <span className="fcr-ai-tag">Vendor decision-support — unverified, confirm against policy</span>}
          {b.kind === 'demographic_likely' && <span className="fcr-ai-tag fcr-demographic-tag">Likely for a patient like this — confirm the diagnosis</span>}
          {b.title && <b className="fcr-block-title">{b.title}</b>}
          {b.body && <p className="fcr-block-body">{b.body}</p>}
          {b.citation && b.severity !== 'unverified' && (
            <div className="fcr-citation">NCD {b.citation.ncd}{b.citation.ncd_desc ? ` · ${b.citation.ncd_desc}` : ''} (CMS)</div>
          )}
        </div>
        );
      })}

      {/* Options — chart-derived covered diagnoses first */}
      {loading && <div className="fcr-loading">Loading suggestions…</div>}
      {!loading && chartOptions.length > 0 && (
        <div className="fcr-section">
          <div className="fcr-group-head">{chartGrounded ? "This patient's covered diagnoses" : "On this patient's chart"}</div>
          <div className="fcr-options">
            {chartOptions.map((o, i) => {
              // Plain "✓ covered" means BOTH facts hold: the code is covered for this
              // test AND the chart shows the patient has it. When only the second
              // holds (an ungrounded tier's candidate the problem list matched), say
              // that and nothing more — the coverage was never checked, so neither
              // green nor "likely — confirm" (the diagnosis is not a guess here).
              const covered = o.status === 'covered';
              return (
                <button key={`c${i}`} type="button"
                  className={`fcr-opt ${covered ? 'fcr-opt-covered' : 'fcr-opt-unverified'}`}
                  onClick={() => onPick(o.value)} title={o.label}>
                  <span className={`fcr-opt-mark${covered ? '' : ' fcr-opt-mark-unverified'}`}>
                    {covered
                      ? (o.demographicLikely ? '✓ covered · likely — confirm' : '✓ covered')
                      : 'On chart · coverage unverified'}
                  </span>
                  <span className="fcr-opt-label">{o.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Search box (provider-gated) */}
      {searchEnabled && (
        <div className="fcr-section">
          <input
            type="text"
            className="fcr-search"
            value={term}
            placeholder={(context.search && context.search.placeholder) || 'Search…'}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => {
              // Enter fires the search immediately (don't wait out the debounce) —
              // the clinician expects pressing Enter on a typed dx to run it.
              if (e.key === 'Enter') {
                e.preventDefault();
                const v = term.trim();
                if (v && onSearch) { lastSearched.current = v; onSearch(v); }
              }
            }}
            aria-label="Search alternatives"
          />
          {context && context.coverageChecked === false && (
            <div className="fcr-note">Coverage not checked for this test — verify before signing.</div>
          )}
        </div>
      )}

      {/* Other options — search hits / AI-advisory candidates */}
      {!loading && otherOptions.length > 0 && (
        <div className="fcr-section">
          {searchEnabled && <div className="fcr-group-head">Matches</div>}
          <div className="fcr-options">
            {otherOptions.map((o, i) => {
              const isVendor = o.source === 'vendor';
              const isAi = o.source === 'ai';
              // Three distinct tones: AI best-guess = INDIGO, vendor decision-support = AMBER,
              // covered = GREEN. AI takes its own class; vendor + status:'unverified' stay amber.
              const unverified = o.status === 'unverified' || isVendor; // amber bucket (vendor + generic unverified)
              const optClass = isAi
                ? 'fcr-opt-ai'
                : (unverified ? 'fcr-opt-unverified' : (o.status === 'covered' ? 'fcr-opt-covered' : ''));
              // demographicLikely keeps the tier color but swaps the mark to say "likely —
              // confirm": a covered code is a FACT about the test, not proof the patient has it.
              const likely = o.demographicLikely === true;
              return (
                <button key={`o${i}`} type="button"
                  className={`fcr-opt ${optClass}`}
                  onClick={() => onPick(o.value)} title={o.label}>
                  {o.status === 'covered' && !isVendor && !isAi && (
                    <span className="fcr-opt-mark">{likely ? '✓ covered · likely — confirm' : '✓ covered'}</span>
                  )}
                  {isAi && (
                    <span className="fcr-opt-mark fcr-opt-mark-ai">{likely ? '✨ AI guess · likely — confirm' : '✨ AI guess · verify'}</span>
                  )}
                  {/* A courtesy list is one lab's published aide, and this rail also
                      renders it on another vendor's order — naming the publisher read
                      as the ORDER's lab ("Northwell · decision-support" beside a Quest
                      home-draw). Label what the option IS, not whose list it came from. */}
                  {!isAi && isVendor && (
                    <span className="fcr-opt-mark fcr-opt-mark-unverified">{likely ? 'Lab decision-support · likely — confirm' : 'Lab decision-support · verify'}</span>
                  )}
                  {!isAi && !isVendor && unverified && (
                    <span className="fcr-opt-mark fcr-opt-mark-unverified">{likely ? 'Unverified · likely — confirm' : 'Unverified · verify'}</span>
                  )}
                  <span className="fcr-opt-label">{o.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!loading && options.length === 0 && blocks.length === 0 && (
        <div className="fcr-note">No additional suggestions for this field.</div>
      )}
    </div>
  );
}
